// crewbit-admin/src/components/money/settings/PeoplePanel.tsx
//
// §Money.StubActionSweepCompletion Phase 4 (Decision #577). People group
// sub-screen body:
//   - InviteAccountantCard (functional invite via team_invites + role='accountant').
//   - Team money permissions — read-only hardcoded role matrix v1. Per-member
//     custom permissions deferred.
//
// Rows reflect actual users.role enum (no separate `operator` role) plus an
// employment_type='operator' sub-row for Money-permission distinctions
// specific to operator employment status.
//
// Mirrors crewbit-mobile/components/money/settings/PeoplePanel.tsx.

import { Users } from 'lucide-react';
import { palette, radii, spacing, typography } from '../../../styles/tokens';
import InviteAccountantCard from './InviteAccountantCard';

type PermKey =
  | 'view_invoices'
  | 'create_invoices'
  | 'mark_paid'
  | 'refund'
  | 'write_off'
  | 'view_revenue'
  | 'view_expenses'
  | 'categorize_expenses'
  | 'manage_tax'
  | 'invite_accountant';

type RoleColumn = 'owner' | 'manager' | 'employee' | 'operator' | 'accountant';

const PERMS: { key: PermKey; label: string }[] = [
  { key: 'view_invoices',       label: 'View invoices' },
  { key: 'create_invoices',     label: 'Create invoices' },
  { key: 'mark_paid',           label: 'Mark paid' },
  { key: 'refund',              label: 'Refund' },
  { key: 'write_off',           label: 'Write off (bad debt)' },
  { key: 'view_revenue',        label: 'View revenue reports' },
  { key: 'view_expenses',       label: 'View expenses' },
  { key: 'categorize_expenses', label: 'Categorize expenses' },
  { key: 'manage_tax',          label: 'Manage tax remittances' },
  { key: 'invite_accountant',   label: 'Invite accountant' },
];

const ROLE_COLUMNS: { key: RoleColumn; label: string; sublabel?: string }[] = [
  { key: 'owner',      label: 'Owner' },
  { key: 'manager',    label: 'Manager' },
  { key: 'employee',   label: 'Employee' },
  { key: 'operator',   label: 'Employee', sublabel: 'Operator' },
  { key: 'accountant', label: 'Accountant' },
];

// Decision #577 hardcoded matrix v1.
const MATRIX: Record<PermKey, Record<RoleColumn, boolean>> = {
  view_invoices:       { owner: true,  manager: true,  employee: true,  operator: true,  accountant: true  },
  create_invoices:     { owner: true,  manager: true,  employee: false, operator: true,  accountant: false },
  mark_paid:           { owner: true,  manager: true,  employee: false, operator: true,  accountant: false },
  refund:              { owner: true,  manager: true,  employee: false, operator: false, accountant: false },
  write_off:           { owner: true,  manager: true,  employee: false, operator: false, accountant: false },
  view_revenue:        { owner: true,  manager: true,  employee: false, operator: false, accountant: true  },
  view_expenses:       { owner: true,  manager: true,  employee: true,  operator: true,  accountant: true  },
  categorize_expenses: { owner: true,  manager: true,  employee: true,  operator: true,  accountant: true  },
  manage_tax:          { owner: true,  manager: true,  employee: false, operator: false, accountant: true  },
  invite_accountant:   { owner: true,  manager: false, employee: false, operator: false, accountant: false },
};

export default function PeoplePanel() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
      <InviteAccountantCard />

      <div style={{
        background: palette.white,
        borderRadius: radii.lg,
        border: `1px solid ${palette.g150}`,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap',
          padding: spacing.md, paddingBottom: spacing.sm,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 18,
            background: palette.accentWash,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Users size={18} color={palette.accent} strokeWidth={2} />
          </div>
          <span style={{ flex: 1, minWidth: 0, fontSize: typography.size.heading, fontWeight: 700, color: palette.g800 }}>
            Team money permissions
          </span>
          <span style={{
            padding: '2px 10px', borderRadius: 999,
            background: palette.g100, color: palette.g600,
            fontSize: 11, fontWeight: 600,
          }}>
            Read-only · v1
          </span>
        </div>
        <div style={{ paddingInline: spacing.md, fontSize: typography.size.caption, color: palette.g500, marginBottom: spacing.sm }}>
          What each role can do in Money. Per-member custom permissions are coming in a future release.
        </div>

        <div style={{ overflowX: 'auto', padding: `0 ${spacing.md}px ${spacing.md}px ${spacing.md}px` }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse',
            fontSize: typography.size.caption, color: palette.g800,
          }}>
            <thead>
              <tr>
                <th style={thStyle}></th>
                {ROLE_COLUMNS.map((c) => (
                  <th key={c.key} style={{ ...thStyle, textAlign: 'center' }}>
                    <div style={{ fontWeight: 600, color: palette.g800 }}>{c.label}</div>
                    {c.sublabel && (
                      <div style={{ fontSize: 10.5, fontWeight: 500, color: palette.g500, marginTop: 1 }}>
                        ({c.sublabel})
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERMS.map((p) => (
                <tr key={p.key}>
                  <td style={tdLabelStyle}>{p.label}</td>
                  {ROLE_COLUMNS.map((c) => (
                    <td key={c.key} style={tdCellStyle}>
                      {MATRIX[p.key][c.key] ? (
                        <span style={{ color: palette.green, fontWeight: 700 }}>✓</span>
                      ) : (
                        <span style={{ color: palette.g300 }}>—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '8px 4px',
  borderBottom: `1px solid ${palette.g150}`,
  fontWeight: 500,
  color: palette.g600,
  fontSize: 11.5,
  textAlign: 'left',
};

const tdLabelStyle: React.CSSProperties = {
  padding: '8px 4px',
  borderBottom: `1px solid ${palette.g100}`,
  color: palette.g700,
  fontSize: 12.5,
};

const tdCellStyle: React.CSSProperties = {
  padding: '8px 4px',
  borderBottom: `1px solid ${palette.g100}`,
  textAlign: 'center',
  fontSize: 14,
};
